// import logException from '@util/js-error';
import * as Sentry from '@sentry/browser';
import { getToken } from '@util/auth-token';
import { confirmAPIVersion } from '@util/api-mismatch';

/**
 * @overview A util for caching API results.
 * @module
 */

export const checkStatus = async response => {
  confirmAPIVersion(response.headers);

  if (response.status >= 200 && response.status < 300) {
    return response;
  }

  if (response.status === 401) {
    let token;

    // Leaving this in in case we want to start reporting
    // const contentType = response.headers.get('content-type');
    // let message;
    // if (contentType === 'application/json') {
    //   message = await response.json();
    // } else {
    //   message = await response.text();
    // }

    try {
      token = await getToken();
    } catch (err) {
      token = `Token error: ${err.message}`;
    }

    Sentry.addBreadcrumb({
      type: 'debug',
      message: `401 with token: ${token}`,
      level: 'info'
    });

    const error = new Error('Not Authenticated');
    // Leaving this in in case we want to start reporting
    // these errors to sentry again.
    //
    // logException(error, {
    //   message,
    //   status: response.status,
    //   url: response.url
    // });

    throw error;
  }

  try {
    const data = await response.json();

    const error = new Error(response?.statusText || 'No status text provided.');
    error.data = data;
    error.url = response.url;
    error.status = response.status;
    throw error;
  } catch (ex) {
    ex.status = response.status;
    ex.url = response.url;
    if (response.status === 404) {
      ex.data = { exceptionText: `${response.url} does not exist.` };
    } else {
      ex.data = ex.data || { exceptionText: ex.toString() };
    }
    throw ex;
  }
};

/**
 * @const {Object} cache a singleton object that contains all of the
 * promises related to fetching resources from the server.
 * The structure of the cache is
 * ```
 *  {
 *    rootKey: {
 *      subKey: Promise
 *    },
 *    rootKey2: {
 *      subKey2: Promise
 *    }
 *  }
 * ```
 * A `rootKey` is the base path to a resource. For example:
 * `/awareapi2/portfolios` or `/appraiseAPI/appraiseusers`
 *
 * A `subKey` is _everything after the `rootKey`_. This includes
 * sub paths and querystrings. For example, the request:
 * `/awareapi2/portfolios/1/checkpoints?$select=LinkedId` would
 * have a rootKey of `/awareapi2/portfolios` and a subKey of
 * `1/checkpoints?$select=LinkedId`.
 *
 */
const cache = {};

/**
 * Takes a url and divides it into a `rootKey` and
 * `subKey` as described above.
 *
 * @param {string} url URL that will be requested via `fetch`.
 *
 * @returns {object} An obejct with a `rootKey` and `subKey` field.
 */
const getKeys = url => {
  const [base, qs] = url.split('?');
  const [, api, resource, ...rest] = base.split('/');
  return {
    rootKey: `/${api}/${resource}`,
    subKey: `${rest.join('/')}?${qs}`
  };
};

/**
 * Looks in the cache and sees if there's already
 * a Promise for a particular URL.
 *
 * @param {string} url URL that will be requested via `fetch`.
 *
 * @returns {Promise} Will either return the Promise
 *                               in the cache or `false`
 */
const checkCache = url => {
  const { rootKey, subKey } = getKeys(url);
  return cache[rootKey] && cache[rootKey][subKey];
};

/**
 * Will add a Promise to the cache for a
 * given url at the `rootKey` and `subKey` from the url.
 *
 * @param {string} url   URL that will be requested via `fetch`.
 * @param {Promise} value A Promise from a fetch call.
 *
 * @returns {Promise} Returns the Promise set in the cache.
 */
const setCache = async (url, value) => {
  const { rootKey, subKey } = getKeys(url);
  const data = await value.then(checkStatus).then(result => {
    if (result.status === 204) {
      /**
       * A `204` result means `No Content`. Therefore, we should
       * should not attempt to parse a JSON response. Downstream
       * functions need to explicitly handle a `null` (`No Content`)
       * response in this case.
       * */

      return null;
    }

    return result.json().then(json => {
      if (!json.data) {
        return json;
      }
      return json.data;
    });
  });

  if (!cache[rootKey]) {
    cache[rootKey] = {};
  }

  cache[rootKey][subKey] = data;

  return cache[rootKey][subKey];
};

/**
 * Clears an a `rootKey` from the
 * cache. This is to force the app to re-fetch
 * any resource related to the resource
 * updated on the server.
 *
 * @param {string} url URL that will be requested via `fetch`.
 *
 * @returns {undefined} undefined
 */
const clearCache = url => {
  const { rootKey } = getKeys(url);
  delete cache[rootKey];
};

/**
 * For a give url, will either return
 * a Promise set in the cache for that url OR make a
 * fetch call and set that Promise into the cache and
 * return the Promise.
 *
 * @param {string} url   URL that will be requested via `fetch`.
 * @param {function} value A function, that calls fetch when executed.
 * @param {boolean} bypassCache should bypass the cache and always call value
 *
 * @returns {Promise} the result of the fetch call.
 */
const getFromCache = async (url, value, bypassCache) => {
  if (!bypassCache && checkCache(url)) {
    return checkCache(url);
  }
  return setCache(url, value());
};

const getValueFromCacheByURL = url => {
  const { rootKey } = getKeys(url);
  if (!cache[rootKey]) return false;
  return cache[rootKey];
};

export { getFromCache, clearCache, getValueFromCacheByURL };
